import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  ClipboardCheck, UserCheck, Vote, RotateCcw, MessageCircleQuestion, Printer, ShieldQuestion, FileText,
} from "lucide-react";
import { adminApi } from "@/lib/api";

/** Phase 11 — Launch Rehearsal: guided dry-run checklist with OBSERVATION NOTES.
 * The launch bar (user-defined): a real person AND a real candidate must each complete
 * their full journey UNAIDED. Do not coach testers — record every hesitation here.
 * Notes persist server-side so Admin review of observations is a real Launch Gate step.
 * Phase 11.9 adds: tester interview ANSWER capture, unresolved-issues log, an explicit
 * Admin review decision, and a PRINT / SAVE REHEARSAL SUMMARY export. */

const TESTER_QUESTIONS = [
  {
    key: "tester-a", title: "Ask Tester A (voter) — only after they finish",
    intro: "Do not coach during the run. Watch where they hesitate — that is the data.",
    questions: [
      { id: "ia1", text: "Did you understand what WitnessYourVote does?" },
      { id: "ia2", text: "Did you understand that your witness does not see your ballot?" },
      { id: "ia3", text: "Did you know what to do next at every screen?" },
      { id: "ia4", text: "Was anything confusing?" },
    ],
  },
  {
    key: "tester-b", title: "Ask Tester B (candidate) — only after they finish",
    intro: "Same rule: watch silently, ask afterwards.",
    questions: [
      { id: "ib1", text: "Did you understand the deadline?" },
      { id: "ib2", text: "Did you know whether your answer was public?" },
      { id: "ib3", text: "Did you understand candidate verification?" },
      { id: "ib4", text: "Did anything make it appear WitnessYourVote endorsed or ranked candidates?" },
    ],
  },
];

const SECTIONS = [
  {
    key: "voter",
    icon: Vote,
    title: "Journey 1 — The Unaided Voter",
    goal: "A stranger can arrive → understand → declare → verify email → get witnessed → receive a receipt → verify it later, with no help from Steve or an administrator.",
    items: [
      { id: "v1", text: "Arrive at the homepage cold: within 30 seconds the tester can say what WitnessYourVote is and what it is NOT (not an official ballot)" },
      { id: "v2", text: "Find their ward from their address (Find My Ward) without guidance" },
      { id: "v3", text: "Start a declaration and complete every form field without asking a question" },
      { id: "v4", text: "Understand the email verification step; locate the verification link (admin: simulated email log) and complete it" },
      { id: "v5", text: "Understand what a witness is and successfully create/share a witness link" },
      { id: "v6", text: "A second tester witnesses the declaration through the link (no self-witnessing possible)" },
      { id: "v7", text: "Receipt is received; QR code opens the public verification page on a phone" },
      { id: "v8", text: "Public verification shows integrity status WITHOUT exposing candidate choice, email, address or witness identity" },
      { id: "v9", text: "Tester returns a day later and verifies the receipt again from just the receipt (nothing else remembered)" },
      { id: "v10", text: "Tester can explain in one sentence what the receipt proves — and what it does not prove" },
    ],
  },
  {
    key: "candidate",
    icon: UserCheck,
    title: "Journey 2 — The Unaided Candidate",
    goal: "A real candidate can receive an invitation → prove who they are → answer the identical public question → have that answer published accurately, with the same treatment as every other candidate.",
    items: [
      { id: "c1", text: "Candidate receives their private portal link (via simulated email log during preview) and opens it on their own device" },
      { id: "c2", text: "Portal shows their name, verification status and the note that portal access ≠ verification" },
      { id: "c3", text: "Candidate reads the open community question, sees the exact deadline and the identical 1,500-character limit" },
      { id: "c4", text: "Candidate drafts, edits and submits an answer with an optional source link — unaided" },
      { id: "c5", text: "The answer appears publicly on Ask the Candidates EXACTLY as submitted (character for character)" },
      { id: "c6", text: "Candidate updates the answer; the public page shows an Updated date and the prior version is preserved internally" },
      { id: "c7", text: "Candidate answers at least one Election Transparency question (voluntary) and it appears unscored on their public profile" },
      { id: "c8", text: "Admin verifies the candidate from an authoritative source (official nominated-candidates list) — badge appears with source + date" },
      { id: "c9", text: "The Compare page shows their answers beside other candidates with identical presentation and no ranking" },
      { id: "c10", text: "Candidate confirms they never saw any voter, declaration, witness or private poll information anywhere in the portal" },
    ],
  },
  {
    key: "safety",
    icon: ClipboardCheck,
    title: "Supporting checks (run alongside both journeys)",
    goal: "The safety rails hold while real people use the system.",
    items: [
      { id: "s1", text: "EMAIL MODE banner confirmed SIMULATED before and after the rehearsal (Admin → Email Mode)" },
      { id: "s2", text: "A deliberately wrong receipt code fails verification with a clear, calm message" },
      { id: "s3", text: "A revoked/expired portal link shows the friendly error and a new link restores access" },
      { id: "s4", text: "Report an Error submitted by a tester; tracking reference works on the public status page; admin completes Review → Confirm → Correct" },
      { id: "s5", text: "Mobile phone run-through of both journeys — no horizontal scrolling anywhere" },
      { id: "s6", text: "All rehearsal declarations used @test.example emails and are excluded from public totals" },
      { id: "s7", text: "Audit log shows every admin action taken during the rehearsal" },
    ],
  },
];

const ALL_ITEMS = SECTIONS.flatMap((s) => s.items);

const statusOf = (st) => {
  if (st?.checked) return "PASSED";
  if ((st?.note || "").trim()) return "NEEDS ATTENTION";
  return "NOT COMPLETED";
};

const esc = (s) => String(s || "")
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

/** Rehearsal Kickoff Guide — one-page tester handouts.
 * DELIBERATELY NON-COACHING: only the starting link, the task, and the four
 * post-test questions. The whole point is to see whether the website itself
 * explains what to do. Never list product steps here. */
const buildHandoutHtml = (tester) => {
  const isVoter = tester === "a";
  const origin = window.location.origin;
  const title = isVoter ? "Tester A — Voter" : "Tester B — Candidate";
  const start = isVoter
    ? `<strong>Start here:</strong> WitnessYourVote homepage — <span class="link">${esc(origin)}</span>`
    : `<strong>Start here:</strong> Your private candidate invitation link:<br/>
       <span class="blank">&nbsp;</span><br/>
       <span class="meta">(Provided separately by the Admin — never printed in advance.)</span>`;
  const task = isVoter
    ? "Use the website as if you were a real voter who wants to create and verify a WitnessYourVote declaration. Complete as much of the process as you can without assistance."
    : "Use the website as if you were a candidate receiving this invitation for the first time. Complete the candidate process without assistance.";
  const questions = (isVoter ? TESTER_QUESTIONS[0] : TESTER_QUESTIONS[1]).questions;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>WitnessYourVote — Rehearsal Handout: ${esc(title)}</title>
  <style>
    body { font-family: Georgia, 'Times New Roman', serif; color: #1a202c; margin: 40px; font-size: 15px; line-height: 1.55; }
    h1 { font-size: 21px; margin-bottom: 0; }
    h2 { font-size: 15px; margin: 26px 0 6px; text-transform: uppercase; letter-spacing: 0.05em; }
    .box { border: 2px solid #1a202c; padding: 14px 16px; margin: 14px 0; }
    .meta { color: #4a5568; font-size: 12px; }
    .link { font-family: monospace; font-size: 13px; word-break: break-all; }
    .blank { display: inline-block; width: 100%; border-bottom: 1.5px solid #1a202c; height: 22px; margin-top: 10px; }
    ol li { margin-bottom: 14px; }
    .answer-line { display: block; border-bottom: 1px solid #a0aec0; height: 20px; margin-top: 6px; }
    .foot { margin-top: 28px; font-size: 11px; color: #718096; border-top: 1px solid #cbd5e0; padding-top: 8px; }
    @media print { body { margin: 16mm; } }
  </style></head><body>
  <h1>WitnessYourVote — Rehearsal Handout</h1>
  <p class="meta">${esc(title)} · One page · Please read only this page before starting.</p>
  <div class="box">${start}</div>
  <div class="box"><strong>Your task:</strong> ${esc(task)}</div>
  <p><strong>Complete the process without help.</strong> Nobody will coach you or answer questions during the run.
  If you get stuck, say so out loud — where you hesitate is exactly what we need to learn.
  Please think aloud as you go. There are no wrong answers.</p>
  <h2>Afterwards, your observer will ask you:</h2>
  <ol>
    ${questions.map((q) => `<li>${esc(q.text)}<span class="answer-line"></span></li>`).join("")}
  </ol>
  <p class="foot">Internal rehearsal document — WitnessYourVote launch readiness. This page deliberately
  does not describe the steps: the website itself must explain what to do.</p>
  </body></html>`;
};

/** Build the printable summary. Deliberately contains ONLY rehearsal data —
 * no voter personal data, no tokens, no private links. */
const buildSummaryHtml = (items) => {
  const decision = items["decision"] || {};
  const decisionText = (decision.note || "").trim();
  const unresolved = (items["unresolved"]?.note || "").trim();
  const counts = { PASSED: 0, "NEEDS ATTENTION": 0, "NOT COMPLETED": 0 };
  ALL_ITEMS.forEach((i) => { counts[statusOf(items[i.id])] += 1; });

  const sectionRows = SECTIONS.map((sec) => `
    <h3>${esc(sec.title)}</h3>
    <table>
      <tr><th style="width:52%">Check</th><th style="width:16%">Status</th><th>Observation notes</th></tr>
      ${sec.items.map((it) => {
        const st = items[it.id] || {};
        const s = statusOf(st);
        const cls = s === "PASSED" ? "ok" : s === "NEEDS ATTENTION" ? "warn" : "todo";
        return `<tr><td>${esc(it.text)}</td><td class="${cls}">${s}</td><td>${esc(st.note || "")}</td></tr>`;
      }).join("")}
    </table>`).join("");

  const interviews = TESTER_QUESTIONS.map((t) => `
    <h3>${esc(t.title.replace(" — only after they finish", ""))}</h3>
    <table>
      <tr><th style="width:45%">Question</th><th>Tester's answer</th></tr>
      ${t.questions.map((q) => `<tr><td>${esc(q.text)}</td><td>${esc(items[q.id]?.note || "— not recorded —")}</td></tr>`).join("")}
    </table>`).join("");

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>WitnessYourVote — Launch Rehearsal Summary</title>
  <style>
    body { font-family: Georgia, 'Times New Roman', serif; color: #1a202c; margin: 32px; font-size: 13px; }
    h1 { font-size: 20px; margin-bottom: 2px; }
    h2 { font-size: 15px; margin: 22px 0 6px; border-bottom: 2px solid #1a202c; padding-bottom: 3px; }
    h3 { font-size: 13px; margin: 16px 0 4px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
    th, td { border: 1px solid #cbd5e0; padding: 5px 7px; text-align: left; vertical-align: top; font-size: 11.5px; }
    th { background: #edf2f7; }
    .ok { color: #276749; font-weight: bold; }
    .warn { color: #975a16; font-weight: bold; }
    .todo { color: #9b2c2c; font-weight: bold; }
    .meta { color: #4a5568; font-size: 11px; }
    .decision { border: 2px solid #1a202c; padding: 10px 12px; margin: 10px 0; }
    .foot { margin-top: 24px; font-size: 10px; color: #718096; border-top: 1px solid #cbd5e0; padding-top: 8px; }
    @media print { body { margin: 12mm; } }
  </style></head><body>
  <h1>WitnessYourVote — Launch Rehearsal Summary</h1>
  <p class="meta">Generated: ${esc(new Date().toLocaleString())} · Internal launch-readiness record · Contains rehearsal observations only — no voter personal data, tokens or private links.</p>

  <h2>Result overview</h2>
  <table><tr><th>Passed</th><th>Needs attention</th><th>Not completed</th><th>Total checks</th></tr>
  <tr><td class="ok">${counts.PASSED}</td><td class="warn">${counts["NEEDS ATTENTION"]}</td><td class="todo">${counts["NOT COMPLETED"]}</td><td>${ALL_ITEMS.length}</td></tr></table>

  <div class="decision">
    <strong>Admin review decision:</strong> ${esc(decisionText || "NOT YET RECORDED")}<br/>
    <span class="meta">Decision recorded: ${esc(decisionText ? fmtDate(decision.updated_at) : "—")}${decision.updated_by && decisionText ? " — by " + esc(decision.updated_by) : ""}</span><br/>
    <span class="meta">Completion date (all checks + decision): ${esc(decisionText && counts["NOT COMPLETED"] === 0 ? fmtDate(decision.updated_at) : "not yet complete")}</span>
  </div>

  <h2>Unresolved usability issues</h2>
  <p>${esc(unresolved) || "— none recorded —"}</p>

  <h2>Checklist (${ALL_ITEMS.length} checks)</h2>
  ${sectionRows}

  <h2>Tester interviews</h2>
  ${interviews}

  <p class="foot">This summary records the human Launch Rehearsal only. It does not change any Launch Gate:
  gates are changed exclusively on the Launch Gate page and every change is recorded in the append-only Gate History Log.
  An automated green test is not a substitute for a real person completing the workflow unaided.</p>
  </body></html>`;
};

const NoteBox = ({ itemId, initial, onSaved, placeholder }) => {
  const [note, setNote] = useState(initial || "");
  const saved = useRef(initial || "");
  const save = async () => {
    if (note.trim() === saved.current.trim()) return;
    try {
      await adminApi.patch("/admin/launch-gate/rehearsal-notes", { item_id: itemId, note });
      saved.current = note;
      toast.success("Saved.");
      onSaved();
    } catch { toast.error("Could not save."); }
  };
  return (
    <Textarea
      className="mt-2 min-h-14 text-xs bg-amber-50/40 border-amber-200 placeholder:text-amber-700/50"
      placeholder={placeholder || "Observation: where did the tester hesitate, re-read, or ask for help?"}
      value={note} maxLength={2000}
      onChange={(e) => setNote(e.target.value)}
      onBlur={save}
      data-testid={`rehearsal-note-${itemId}`}
    />
  );
};

export default function Rehearsal() {
  const [items, setItems] = useState({});
  const [loaded, setLoaded] = useState(false);

  const load = () => {
    adminApi.get("/admin/launch-gate/rehearsal-notes")
      .then((r) => { setItems(r.data.items || {}); setLoaded(true); })
      .catch(() => setLoaded(true));
  };
  useEffect(() => { load(); }, []);

  const toggle = async (id, value) => {
    setItems((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), checked: value } }));
    try {
      await adminApi.patch("/admin/launch-gate/rehearsal-notes", { item_id: id, checked: value });
    } catch { toast.error("Could not save."); }
  };

  const reset = async () => {
    for (const item of ALL_ITEMS) {
      if (items[item.id]?.checked || items[item.id]?.note) {
        // eslint-disable-next-line no-await-in-loop
        await adminApi.patch("/admin/launch-gate/rehearsal-notes",
                             { item_id: item.id, checked: false, note: "" }).catch(() => {});
      }
    }
    load();
    toast.success("Checklist reset. Interview answers and the review decision are kept.");
  };

  const setDecision = async (text) => {
    try {
      await adminApi.patch("/admin/launch-gate/rehearsal-notes", { item_id: "decision", note: text });
      toast.success("Review decision recorded.");
      load();
    } catch { toast.error("Could not save decision."); }
  };

  const printSummary = () => {
    const w = window.open("", "_blank", "width=920,height=760");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups to print the summary."); return; }
    w.document.write(buildSummaryHtml(items));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const printHandout = (tester) => {
    const w = window.open("", "_blank", "width=920,height=760");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups to print the handout."); return; }
    w.document.write(buildHandoutHtml(tester));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const done = ALL_ITEMS.filter((i) => items[i.id]?.checked).length;
  const pct = Math.round((done / ALL_ITEMS.length) * 100);
  const decision = (items["decision"]?.note || "").trim();

  if (!loaded) return null;

  return (
    <div data-testid="admin-rehearsal-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground inline-flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 text-accent" /> Launch Rehearsal
          </h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            The launch bar is not feature count. It is: can a real person and a real candidate each
            complete their full journey unaided? Run these with real testers, tick as you go, and
            record every hesitation in the observation notes (saved server-side for the Launch Gate
            "observations reviewed" step).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => printHandout("a")}
                  data-testid="rehearsal-handout-a-btn">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> Tester A handout
          </Button>
          <Button size="sm" variant="outline" onClick={() => printHandout("b")}
                  data-testid="rehearsal-handout-b-btn">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> Tester B handout
          </Button>
          <Button size="sm" className="bg-primary text-white hover:bg-primary/90" onClick={printSummary}
                  data-testid="rehearsal-print-btn">
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Print / Save Rehearsal Summary
          </Button>
          <Button size="sm" variant="outline" onClick={reset} data-testid="rehearsal-reset-btn">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset checklist
          </Button>
        </div>
      </div>

      <Card className="mb-6 border-l-4 border-l-accent" data-testid="rehearsal-kickoff-card">
        <CardContent className="p-4">
          <p className="text-xs text-foreground">
            <strong>Kickoff guides:</strong> hand each tester their one-page handout before they start.
            The handouts contain <strong>only</strong> the starting link, the task, and the four post-test
            questions — never the steps. The whole point is to see whether the website itself explains
            what to do. Tester B's private invitation link is written in by hand at handoff, never printed
            in advance.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardContent className="p-4 flex items-center gap-4">
          <Progress value={pct} className="h-2 flex-1" data-testid="rehearsal-progress" />
          <Badge variant="outline"
                 className={pct === 100 ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-secondary text-foreground border-slate-300"}
                 data-testid="rehearsal-progress-badge">
            {done} / {ALL_ITEMS.length} {pct === 100 ? "— READY" : ""}
          </Badge>
        </CardContent>
      </Card>

      <div className="space-y-6">
        {SECTIONS.map((section) => {
          const Icon = section.icon;
          const secDone = section.items.filter((i) => items[i.id]?.checked).length;
          return (
            <Card key={section.key} data-testid={`rehearsal-section-${section.key}`}>
              <CardContent className="p-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="font-heading font-bold text-lg text-foreground inline-flex items-center gap-2">
                    <Icon className="h-5 w-5 text-accent" /> {section.title}
                  </h2>
                  <Badge variant="outline" className="bg-secondary text-foreground border-slate-300 text-[10px]">
                    {secDone} / {section.items.length}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1 max-w-3xl italic">{section.goal}</p>
                <div className="mt-4 space-y-2.5">
                  {section.items.map((item) => (
                    <div key={item.id}
                         className="rounded-lg border border-slate-200 p-3 hover:bg-secondary/50 transition-colors"
                         data-testid={`rehearsal-item-${item.id}`}>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox checked={!!items[item.id]?.checked}
                                  onCheckedChange={(v) => toggle(item.id, !!v)}
                                  className="mt-0.5"
                                  data-testid={`rehearsal-check-${item.id}`} />
                        <span className={`text-sm leading-relaxed ${items[item.id]?.checked ? "text-muted-foreground line-through" : "text-foreground"}`}>
                          {item.text}
                        </span>
                      </label>
                      <NoteBox itemId={item.id} initial={items[item.id]?.note} onSaved={load} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Post-run tester interviews — questions AND answer capture */}
        <div className="grid lg:grid-cols-2 gap-6">
          {TESTER_QUESTIONS.map((t) => (
            <Card key={t.key} className="border-l-4 border-l-accent" data-testid={`rehearsal-interview-${t.key}`}>
              <CardContent className="p-5">
                <h3 className="font-heading font-bold text-sm text-foreground inline-flex items-center gap-2">
                  <MessageCircleQuestion className="h-4 w-4 text-accent" /> {t.title}
                </h3>
                <p className="text-[11px] text-muted-foreground mt-1 italic">{t.intro}</p>
                <ol className="mt-3 space-y-3 list-decimal list-inside">
                  {t.questions.map((q) => (
                    <li key={q.id} className="text-sm text-foreground">
                      {q.text}
                      <NoteBox itemId={q.id} initial={items[q.id]?.note} onSaved={load}
                               placeholder="Tester's answer — record verbatim where possible" />
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Admin review — unresolved issues + explicit decision */}
        <Card className="border-2 border-primary/30" data-testid="rehearsal-review-card">
          <CardContent className="p-6">
            <h2 className="font-heading font-bold text-lg text-foreground inline-flex items-center gap-2">
              <ShieldQuestion className="h-5 w-5 text-accent" /> Admin review
            </h2>
            <p className="text-[11px] text-muted-foreground mt-1">
              Recording a decision here creates the permanent review record for the printable summary.
              It does <strong>not</strong> turn any Launch Gate green — gates are changed only on the
              Launch Gate page, and every gate change is written to the append-only Gate History Log.
            </p>
            <div className="mt-4">
              <p className="text-xs font-semibold text-foreground">Unresolved usability issues (leave empty when none remain)</p>
              <NoteBox itemId="unresolved" initial={items["unresolved"]?.note} onSaved={load}
                       placeholder="List any point where a tester could not proceed unaided that is still unfixed" />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white"
                      onClick={() => setDecision("APPROVED — rehearsal observations reviewed")}
                      data-testid="rehearsal-approve-btn">
                Approve rehearsal
              </Button>
              <Button size="sm" variant="outline" className="text-destructive border-destructive/40"
                      onClick={() => setDecision("NEEDS WORK — issues must be resolved and re-tested")}
                      data-testid="rehearsal-needswork-btn">
                Needs work
              </Button>
              <Badge variant="outline"
                     className={decision.startsWith("APPROVED")
                       ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                       : decision ? "bg-red-50 text-red-800 border-red-300"
                       : "bg-secondary text-muted-foreground border-slate-300"}
                     data-testid="rehearsal-decision-badge">
                {decision || "No decision recorded yet"}
                {decision && items["decision"]?.updated_at
                  ? ` — ${new Date(items["decision"].updated_at).toLocaleDateString()}` : ""}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      <p className="text-[11px] text-muted-foreground italic mt-6">
        If both journeys complete cleanly with all supporting checks green, WitnessYourVote is ready
        to move from a sophisticated build into a real public platform. Keep EMAIL MODE: SIMULATED
        until TEST MODE is explicitly authorized with provider credentials.
      </p>
    </div>
  );
}
